import { useEffect, useState } from 'react'
import { Alert, Box, LinearProgress, Skeleton, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import SectionContainer from '@components/SectionContainer'
import API from '@enums/api'
import type { FreeHoursSnapshot } from '@types'
import { formatDurationHoursMinutes } from '@utils'

interface Props {
    /**
     * The Habitat Accounts id, which is also the Messenger Pigeon user id —
     * the two tables share it, which is how the All Users grid knows who has
     * an account.
     */
    userId: string
}

/** This panel counts in milliseconds; the shared formatter takes seconds. */
const formatDuration = (ms: number) => formatDurationHoursMinutes(ms / 1000)

function Row({ label, value }: { label: string; value: string }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.5,
            }}
        >
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
                {value}
            </Typography>
        </Box>
    )
}

/**
 * The one sentence support actually needs. Every branch here is a case where
 * the raw numbers would mislead: no cap at all, a cap that can never be
 * refreshed, and ordering that does nothing until the allowance runs out.
 */
function summarise(snapshot: FreeHoursSnapshot): {
    text: string
    severity: 'success' | 'info' | 'warning'
} {
    if (snapshot.subscribed) {
        return {
            text: 'Subscribed, so there is no recording limit.',
            severity: 'success',
        }
    }

    if (snapshot.instructor) {
        return {
            text: 'An instructor, so there is no recording limit.',
            severity: 'success',
        }
    }

    if (!snapshot.canRefresh) {
        return {
            text: `A personal account created on or after ${DateTime.fromISO(snapshot.limitCutOffDate).toFormat('d LLL yyyy')}, so ordering notes will not bring these hours back. They would need to subscribe.`,
            severity: 'warning',
        }
    }

    if (snapshot.refreshPending) {
        return {
            text: 'They have ordered enough notes. Their hours come back the next time they open Messenger Pigeon.',
            severity: 'success',
        }
    }

    if (!snapshot.exhausted) {
        return {
            text: `${formatDuration(snapshot.remainingMs)} of recording left. Ordering notes only earns hours back once all ${snapshot.limitHours} are used.`,
            severity: 'info',
        }
    }

    return {
        text: `All ${snapshot.limitHours} hours used. They need to order ${formatDuration(snapshot.orderedNeededMs)} more of notes to get them back.`,
        severity: 'warning',
    }
}

/** The populated panel. Split out so the summary is worked out once. */
function Details({ snapshot }: { snapshot: FreeHoursSnapshot }) {
    const summary = summarise(snapshot)
    const neverRefreshed = Date.parse(snapshot.lastRefreshedAt) === 0

    return (
        <Box sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left' }}>
            <Alert severity={summary.severity} sx={{ mb: 2 }}>
                {summary.text}
            </Alert>

            {snapshot.capped && (
                <>
                    <Row
                        label="Recording used"
                        value={`${formatDuration(snapshot.recordedMs)} of ${snapshot.limitHours}h`}
                    />
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(
                            100,
                            (snapshot.recordedMs / snapshot.limitMs) * 100
                        )}
                        sx={{ my: 1 }}
                    />
                    <Row
                        label="Notes ordered since reset"
                        value={`${formatDuration(snapshot.orderedMs)} of ${formatDuration(snapshot.orderThresholdMs)}`}
                    />
                    <Row
                        label="Still to order for a reset"
                        value={
                            snapshot.canRefresh
                                ? formatDuration(snapshot.orderedNeededMs)
                                : 'Cannot reset'
                        }
                    />
                    <Row
                        label="Counting since"
                        value={
                            neverRefreshed
                                ? 'Account opened'
                                : DateTime.fromISO(
                                      snapshot.lastRefreshedAt
                                  ).toFormat('d LLL yyyy')
                        }
                    />
                    {!snapshot.lastRefreshStamped && !neverRefreshed && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            They have never been reset, so this counts from
                            their most recent order.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    )
}

/**
 * Free recording allowance, the question support is asked most and could
 * previously only answer by asking a developer to query the database.
 */
export default function FreeRecording({ userId }: Props) {
    const [snapshot, setSnapshot] = useState<FreeHoursSnapshot | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [noAccount, setNoAccount] = useState(false)

    useEffect(() => {
        let cancelled = false

        async function load() {
            setError(null)
            setNoAccount(false)
            setSnapshot(null)

            const res = await API.mp.users.getFreeHours(userId)
            if (cancelled) return

            if (!res.ok) {
                /*
                 * Plenty of people here have never used Messenger Pigeon —
                 * the All Users grid has a column for exactly that. Saying so
                 * is the answer, where a red failure box would read as the
                 * page being broken.
                 */
                if (res.status === 404) {
                    setNoAccount(true)
                    return
                }
                setError(`${res.status} ${res.message}`)
                return
            }
            setSnapshot(res.value)
        }

        load()
        return () => {
            cancelled = true
        }
    }, [userId])

    return (
        <SectionContainer title="Free Recording">
            {error && <Alert severity="error">{error}</Alert>}

            {noAccount && (
                <Alert severity="info">
                    No Messenger Pigeon account, so there is nothing to record
                    against.
                </Alert>
            )}

            {!error && !noAccount && !snapshot && (
                <Skeleton variant="rounded" height={120} />
            )}

            {snapshot && <Details snapshot={snapshot} />}
        </SectionContainer>
    )
}
